import React, {useEffect, useMemo, useRef, useState} from 'react'
import DropdownSelect from "../../../../../components-new/form/DropdownSelect";
import DatePicker from "../../../../../components-new/form/DatePicker";
import Popup from "../../../../../components-new/ui/Popup";
import config from "../../../../../config";
import Translate, {t} from '../../../../../components/Translate';
import SearchInput from "../../../../../components-new/form/SearchInput";
import BlueButton from "../../../../../components-new/buttons/BlueButton";
import SlidersHorizontalIcon from "../../../../../assets/icons/SlidersHorizontalIcon";
import colors from "../../../../../utils/Colors";
import ResetButton from "../../../../../components-new/buttons/ResetButton";
import ContactAvatar from "../../../../../components-new/general/ContactAvatar";
import PlatformIcon from "../../../../../components-new/general/PlatformIcon";
import BotAvatar from "../clients/new/overview/BotAvatar";


const TableFilters = ({
                          user,
                          onFilterChange,
                          company,
                          children,
                          isFetching
                      }) => {

    const {subs, members} = company;

    const statusesOptions = config.conversationStatusesList
        .map(data => ({
            value: data,
            label: t({text: data, user}),
        }));

    const companiesOptions = subs.map((sub) => ({
        value: sub.id,
        label: sub.name,
        client: sub,
    }));
    const operatorOptions = [
        {
            value: -1,
            label: t({user, text: "noOperator"}),
            contact: {},
        },
        ...members.map(m => ({
            value: m.id,
            label: `${m.firstName || ''} ${m.lastName || ''}`.trim(),
            contact: m,
        }))
    ]

    const platformOptions = Object.values(config.platformsMap)
        .filter(p => p !== 'COMPANY' && p !== 'VIBER') // TODO remove viber from filtering when integration is finished
        .map(p => ({
            value: p,
            label: p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()
        }))

    const defaultFilters = {
        searchValue: "",
        categoryName: "",
        userKey: "",
        companies: companiesOptions,
        operators: operatorOptions,
        platforms: platformOptions,
        statuses: statusesOptions,
        startDate: null,
        endDate: null,
    }

    const [filters, setFilters] = useState(defaultFilters);

    const [searchValue, setSearchValue] = useState("");
    const [categoryName, setCategoryName] = useState("");
    const [userKey, setUserKey] = useState("");

    useEffect(() => {
        if (searchValue === filters.searchValue) return
        const handler = setTimeout(() => setFilters(prev => {
            return {
                ...prev, searchValue
            }
        }), 500);
        return () => clearTimeout(handler);
    }, [searchValue]);

    useEffect(() => {
        if (categoryName === filters.categoryName) return
        const handler = setTimeout(() => setFilters(prev => {
            return {
                ...
                    prev, categoryName
            }
        }), 500);
        return () => clearTimeout(handler);
    }, [categoryName]);

    useEffect(() => {
        if (userKey === filters.userKey) return
        const handler = setTimeout(() => setFilters(prev => {
            return {
                ...
                    prev, userKey
            }
        }), 500);
        return () => clearTimeout(handler);
    }, [userKey]);

    const searchValueRef = useRef(null);
    const categoryNameRef = useRef(null);
    const userKeyRef = useRef(null);

    const focusedInputRef = useRef(null);
    const inputRefs = [searchValueRef, categoryNameRef, userKeyRef];

    const changedFiltersCount = useMemo(() => {
        let count = 0;
        if (filters.statuses.length !== statusesOptions.length) count++
        if (filters.companies.length !== companiesOptions.length) count++
        if (filters.operators.length !== operatorOptions.length) count++
        if (filters.platforms.length !== platformOptions.length) count++
        if (filters.categoryName !== "") count++
        if (filters.startDate !== null) count++
        if (filters.endDate !== null) count++

        return count
    }, [filters])

    useEffect(() => {
        const index = inputRefs.findIndex(r => r.current === document.activeElement)

        focusedInputRef.current = index === -1 ? null : inputRefs[index].current;

        onFilterChange({
            searchValue: filters.searchValue,
            categoryName: filters.categoryName,
            userKey: filters.userKey,
            companies: filters.companies.map(c => c.value),
            operatorIds: filters.operators.map(o => o.value),
            platforms: filters.platforms.map(p => p.value),
            statuses: filters.statuses.map(s => s.value),
            startDate: filters.startDate,
            endDate: filters.endDate
        })
    }, [filters]);

    useEffect(() => {
        if (!isFetching && focusedInputRef && focusedInputRef.current) {
            focusedInputRef.current.focus()
        }
    }, [isFetching])

    function handleReset() {
        setFilters(defaultFilters);
        setSearchValue("");
        setUserKey("");
        setCategoryName("");
    }

    function updateFilter(key, value) {
        setFilters(prev => {
            return {
                ...prev,
                [key]: value
            }
        })
    }

    return (
        <div style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            paddingTop: '8px'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                alignItems: 'center',
                flex: '1 1 320px',
                minWidth: 0,
                order: 2
            }}>
                <SearchInput
                    value={searchValue}
                    onChange={e => setSearchValue(e.target.value)}
                    placeholder="searchByMessage"
                    containerStyle={{flex: '1 1 200px', minWidth: 0, maxWidth: '300px'}}
                    style={{width: '100%', height: '36px'}}
                    disabled={isFetching}
                    ref={searchValueRef}
                />
                <SearchInput
                    value={userKey}
                    onChange={e => setUserKey(e.target.value)}
                    placeholder="searchByUser"
                    containerStyle={{flex: '1 1 200px', minWidth: 0, maxWidth: '300px'}}
                    style={{width: '100%', height: '36px'}}
                    disabled={isFetching}
                    ref={userKeyRef}
                />
                {children}
            </div>

            <div style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
                flexShrink: 0,
                order: 1
            }}>
                {
                    (changedFiltersCount !== 0 || searchValue !== '' || userKey !== '')
                    &&
                    <ResetButton
                        onClick={handleReset}
                        width={150}
                        height={36}
                        style={{order: 2}}
                        disabled={isFetching}
                    />
                }
                <Popup
                    trigger={
                        <BlueButton
                            variant="soft"
                            size="md"
                            width="auto"
                            styles={{
                                display: 'flex',
                                gap: '4px',
                                alignItems: 'center',
                                position: 'relative',
                                height: '36px'
                            }}
                        >
                            <React.Fragment>
                                <SlidersHorizontalIcon stroke="currentColor" width={20} height={20}/>
                                <span style={{
                                    fontSize: '14px',
                                    marginLeft: '4px',
                                }}>
                                    <Translate text={'filters'}/>
                                </span>
                                {
                                    changedFiltersCount !== 0 && <div className="filter-count-badge">
                                        {changedFiltersCount}
                                    </div>}
                            </React.Fragment>
                        </BlueButton>
                    }
                    direction="bottom"
                    panelAlign={{horizontal: "right", vertical: ""}}
                    style={{order: 1}}
                    panelStyle={{overflow: 'visible'}}
                    panelInnerStyle={{overflow: 'visible'}}
                >
                    <div style={{width: 1000, overflow: 'visible'}}>
                        <div style={{display: "flex", alignItems: "center", gap: '8px', marginBottom: '8px'}}>
                            <SearchInput
                                value={categoryName}
                                onChange={e => setCategoryName(e.target.value)}
                                placeholder="searchByCategory"
                                containerStyle={{flexGrow: 1}}
                                disabled={isFetching}
                                ref={categoryNameRef}
                                style={{height: '36px'}}
                            />
                            <DatePicker
                                value={filters.startDate}
                                onChange={date => updateFilter('startDate', date)}
                                onReset={() => {
                                    setTimeout(() => {
                                        updateFilter('startDate', null)
                                    }, 100)
                                }}
                                disabled={isFetching}
                                placeholder={'startDate'}
                                popupStyle={{width: 'calc(25% - 6px)'}}
                                inputContainerStyle={{height: '36px'}}
                            />
                            <DatePicker
                                value={filters.endDate}
                                onChange={date => updateFilter('endDate', date)}
                                onReset={() => {
                                    setTimeout(() => {
                                        updateFilter('endDate', null)
                                    }, 100)
                                }}
                                disabled={isFetching}
                                endOfDay
                                placeholder={'endDate'}
                                popupStyle={{width: 'calc(25% - 6px)'}}
                                inputContainerStyle={{height: '36px'}}
                            />
                        </div>

                        <div style={{display: "flex", alignItems: "center", gap: '8px'}}>
                            <DropdownSelect
                                options={companiesOptions}
                                renderLabel={(val) => {
                                    return (
                                        <span>
                                    {
                                        val.length === 0 ?
                                            <span style={{color: colors.silverDark}}><Translate text={'Bots'}/></span>
                                            :
                                            <span><Translate
                                                text={'Bots'}/> {'(' + filters.companies.length + ')'}</span>
                                    }
                                </span>
                                    )
                                }}
                                onChange={v => filters.companies.some(s => s.value === v.value) ?
                                    updateFilter('companies', filters.companies.filter(s => s.value !== v.value))
                                    :
                                    updateFilter('companies', [...filters.companies, v])
                                }
                                selectedValue={filters.companies}
                                searchBy={(option, search) =>
                                    !search || option.label.toLowerCase().includes(search.toLowerCase())
                                }
                                onSelectAll={() => {
                                    filters.companies.length === companiesOptions.length ? updateFilter('companies', []) : updateFilter('companies', companiesOptions);
                                }}
                                variant={'outline'}
                                styles={{
                                    width: '300px',
                                    height: '36px'
                                }}
                                disabled={isFetching}
                            >
                                {(option) => (
                                    <div className="history-filter-option">
                                        <BotAvatar client={option.client} size={26} imgSize={24}/>
                                        <span>{option.label}</span>
                                    </div>
                                )}
                            </DropdownSelect>

                            <DropdownSelect
                                options={operatorOptions}
                                renderLabel={(val) => (
                                    <span>
                                        {val.length === 0 ? (
                                            <span style={{color: colors.silverDark}}>
                                                <Translate text={'operator'}/>
                                            </span>
                                        ) : (
                                            <span>
                                                <Translate text={'operator'}/> {'(' + filters.operators.length + ')'}
                                            </span>
                                        )}
                                    </span>
                                )}
                                onChange={v =>
                                    filters.operators.some(s => s.value === v.value)
                                        ? updateFilter('operators', filters.operators.filter(s => s.value !== v.value))
                                        : updateFilter('operators', [...filters.operators, v])
                                }
                                selectedValue={filters.operators}
                                searchBy={(option, search) =>
                                    !search || option.label.toLowerCase().includes(search.toLowerCase())
                                }
                                onSelectAll={() => {
                                    filters.operators.length === operatorOptions.length
                                        ? updateFilter('operators', [])
                                        : updateFilter('operators', operatorOptions);
                                }}
                                variant={'outline'}
                                styles={{width: '300px', height: '36px'}}
                                disabled={isFetching}
                            >
                                {(option) => (
                                    <div className="history-filter-option">
                                        <ContactAvatar contact={option.contact} size={24}/>
                                        <span>{option.label}</span>
                                    </div>
                                )}
                            </DropdownSelect>


                            <DropdownSelect
                                options={platformOptions}
                                renderLabel={(val) => (
                                    <span>
                                        {val.length === 0 ? (
                                            <span style={{color: colors.silverDark}}>
                                                <Translate text={'platform'}/>
                                            </span>
                                        ) : (
                                            <span>
                                                <Translate text={'platform'}/> {'(' + filters.platforms.length + ')'}
                                            </span>
                                        )}
                                    </span>
                                )}
                                onChange={v =>
                                    filters.platforms.some(s => s.value === v.value)
                                        ? updateFilter('platforms', filters.platforms.filter(s => s.value !== v.value))
                                        : updateFilter('platforms', [...filters.platforms, v])
                                }
                                selectedValue={filters.platforms}
                                searchBy={(option, search) =>
                                    !search || option.value.toLowerCase().includes(search.toLowerCase())
                                }
                                onSelectAll={() => {
                                    filters.platforms.length === platformOptions.length
                                        ? updateFilter('platforms', [])
                                        : updateFilter('platforms', platformOptions);
                                }}
                                variant={'outline'}
                                styles={{width: '300px', height: '36px'}}
                                disabled={isFetching}
                            >
                                {(option) => (
                                    <div className="history-filter-option">
                                        <span className="history-filter-option__platform-icon">
                                            <PlatformIcon conversation={{platform: option.value}} size={22}/>
                                        </span>
                                        <span>{option.label}</span>
                                    </div>
                                )}
                            </DropdownSelect>


                            <DropdownSelect
                                options={statusesOptions}
                                renderLabel={(val) => (
                                    <span>
                                        {val.length === 0 ? (
                                            <span style={{color: colors.silverDark}}>
                                                <Translate text={'statuses'}/>
                                            </span>
                                        ) : (
                                            <span>
                                                <Translate text={'statuses'}/> {'(' + filters.statuses.length + ')'}
                                            </span>
                                        )}
                                    </span>
                                )}
                                onChange={v =>
                                    filters.statuses.some(s => s.value === v.value)
                                        ? updateFilter('statuses', filters.statuses.filter(s => s.value !== v.value))
                                        : updateFilter('statuses', [...filters.statuses, v])
                                }
                                selectedValue={filters.statuses}
                                searchBy={(option, search) =>
                                    !search || option.value.toLowerCase().includes(search.toLowerCase())
                                }
                                onSelectAll={() => {
                                    filters.statuses.length === statusesOptions.length
                                        ? updateFilter('statuses', [])
                                        : updateFilter('statuses', statusesOptions);
                                }}
                                variant={'outline'}
                                styles={{width: '300px', height: '36px'}}
                                disabled={isFetching}
                            />

                        </div>
                    </div>
                </Popup>
            </div>
        </div>
    )
}

export default TableFilters;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/history/TableFilters.jsx